/**
 * Loopback daemon detection — does the user have an Axon daemon
 * running locally?
 *
 * Approach: fetch http://127.0.0.1:54321/health with `mode: "no-cors"`
 * + a 1.5s timeout. The daemon's CORS allowlist is localhost-only
 * (server.ts:289-296), so a request from strategies.axon-trading.com
 * would normally be CORS-blocked — but no-cors mode bypasses the
 * preflight + lets the network call go through. We can't read the
 * response body (opaque), but the fetch promise resolves on
 * network success and rejects on connection failure. That's enough
 * to drive the install CTA.
 *
 * False positive: another local server on port 54321. Acceptable —
 * the worst-case UX is a misleading "Open Axon" button that opens
 * the unrelated server. The user notices in <2s.
 *
 * False negative: daemon running on a different port. Also
 * acceptable — the user can fall through to the CLI-install copy.
 *
 * Why not GET a known endpoint and inspect the body: CORS prevents
 * cross-origin reads from `strategies.axon-trading.com` to
 * `http://127.0.0.1:54321/`. The opaque-response trick is the only
 * way to detect liveness without modifying the daemon's CORS rules.
 */

export interface DaemonStatus {
  reachable: boolean;
  /** ISO timestamp of the most recent probe. Useful for debugging
   *  flaky detection. */
  checkedAt: string;
}

const DAEMON_URL = "http://127.0.0.1:54321";
const PROBE_TIMEOUT_MS = 1500;

export async function detectDaemon(): Promise<DaemonStatus> {
  const checkedAt = new Date().toISOString();
  try {
    await fetch(`${DAEMON_URL}/health`, {
      method: "GET",
      mode: "no-cors",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return { reachable: true, checkedAt };
  } catch {
    // Network error / connection refused / timeout. Daemon is not
    // reachable from the public site.
    return { reachable: false, checkedAt };
  }
}

/** URL of the local Axon dashboard. Opening this in a new tab is
 *  the user's path into the daemon's UI to complete an install. */
export const LOCAL_DASHBOARD_URL = DAEMON_URL;
